import { useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { useDelayedLoadingState } from '@backrs_ui/grow-ui/src/hooks/useDelayedLoadingState';
import {
  useGetMajorsBrowseQuery,
  useGetExploreFiltersQuery,
} from '@backrs_ui/grow-ui/src/redux/majorsGuide/majorsGuide.api';

import { SearchMajorsPage } from './SearchMajorsPage';
import { useAgentContext } from './SearchMajorsPageContainer.agentContext';
import { SearchMajorsFilters } from '../types';

const ITEMS_PER_PAGE = 12;

/**
 * SearchMajorsPageContainer - Container component for the Search Majors page
 *
 * Responsibilities:
 * - Parse URL params into SearchMajorsFilters object
 * - Call useGetMajorsBrowseQuery with filter params
 * - Handle filter changes → update URL → triggers re-query
 * - Handle page changes
 * - Handle major selection (navigates to major detail page)
 * - Render SearchMajorsPage with all data as props
 */
export const SearchMajorsPageContainer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Parse URL params with context-aware defaults (relevance sort when searching)
  const filters: SearchMajorsFilters = useMemo(() => {
    const searchTerm = searchParams.get('search') || '';
    const hasExplicitSort = searchParams.has('sort');
    const defaultSort = searchTerm && !hasExplicitSort ? 'relevance' : 'name';
    const defaultOrder = searchTerm && !hasExplicitSort ? 'desc' : 'asc';

    return {
      search: searchTerm,
      colleges: searchParams.getAll('colleges'),
      sort: (searchParams.get('sort') as SearchMajorsFilters['sort']) || defaultSort,
      order: (searchParams.get('order') as 'asc' | 'desc') || defaultOrder,
      page: parseInt(searchParams.get('page') || '1', 10),
    };
  }, [searchParams]);

  // Fetch majors data
  const { data, isLoading, isFetching } = useGetMajorsBrowseQuery({
    search: filters.search || undefined,
    colleges: filters.colleges.length > 0 ? filters.colleges : undefined,
    sort: filters.sort,
    order: filters.order,
    page: filters.page,
    perPage: ITEMS_PER_PAGE,
  });

  const isFetchingMajors = isLoading || isFetching;
  // Delay surfacing the spinner so fast responses don't flash it.
  const showSpinner = useDelayedLoadingState(isFetchingMajors);

  useAgentContext(filters, data?.total ?? 0, data?.items ?? []);

  // Fetch explore page filter options (college types + sort values)
  const { data: exploreFiltersData } = useGetExploreFiltersQuery(undefined);

  // Map college types to string array for FilterControls
  const collegeOptions = useMemo(
    () => exploreFiltersData?.collegeTypes.map((item) => item.value) ?? [],
    [exploreFiltersData],
  );

  // Handle filter changes
  const handleFilterChange = useCallback(
    (newFilters: Partial<SearchMajorsFilters>) => {
      const updatedParams = new URLSearchParams(searchParams);

      // Update params based on new filter values
      Object.entries(newFilters).forEach(([key, value]) => {
        // Handle array values (like colleges)
        if (Array.isArray(value)) {
          updatedParams.delete(key);
          value.forEach((v) => updatedParams.append(key, v));
        } else if (value === '' || value === undefined || value === null) {
          updatedParams.delete(key);
        } else {
          updatedParams.set(key, String(value));
        }
      });

      // Reset page to 1 when filters change (except when page itself is being changed)
      if (!('page' in newFilters)) {
        updatedParams.delete('page');
      }

      setSearchParams(updatedParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Handle page changes
  const handlePageChange = useCallback(
    (page: number) => {
      const updatedParams = new URLSearchParams(searchParams);

      if (page === 1) {
        updatedParams.delete('page');
      } else {
        updatedParams.set('page', String(page));
      }

      setSearchParams(updatedParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Handle major selection (navigate to major details page)
  const handleMajorSelect = useCallback(
    (id: number) => {
      navigate(`/majors/${id}`);
    },
    [navigate],
  );

  return (
    <SearchMajorsPage
      majors={data?.items ?? []}
      total={data?.total ?? 0}
      page={filters.page}
      isFetching={isFetchingMajors}
      showSpinner={showSpinner}
      filters={filters}
      onFilterChange={handleFilterChange}
      onPageChange={handlePageChange}
      onMajorSelect={handleMajorSelect}
      perPage={ITEMS_PER_PAGE}
      collegeOptions={collegeOptions}
    />
  );
};
